import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Lightning, Phone, ArrowClockwise, X, CheckCircle, Clock, Star, XCircle, Archive, Funnel, FolderOpen, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ClientRecordDialog from '../components/ClientRecordDialog';
import RequestDetailDialog from '../components/RequestDetailDialog';
import ClientContactCard from '../components/ClientContactCard';
import AssignStaffBox from '../components/AssignStaffBox';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { key: 'incoming', label: 'Nuove', statuses: 'notified,pending_pro', icon: Clock, color: 'text-blue-700' },
  { key: 'accepted', label: 'Accettate', statuses: 'accepted,in_progress', icon: CheckCircle, color: 'text-indigo-700' },
  { key: 'completed', label: 'Completate', statuses: 'completed', icon: CheckCircle, color: 'text-sky-700' },
  { key: 'reviewed', label: 'Recensite', statuses: 'reviewed', icon: Star, color: 'text-purple-700' },
  { key: 'cancelled', label: 'Annullate', statuses: 'cancelled', icon: XCircle, color: 'text-red-700' },
  { key: 'closed', label: 'Chiuse', statuses: 'closed_by_pro', icon: Archive, color: 'text-slate-700' },
];

const STATUS_LABEL = {
  notified: 'Notificata', pending_pro: 'Pending', accepted: 'Accettata',
  in_progress: 'In corso', completed: 'Completata', reviewed: 'Recensita',
  cancelled: 'Annullata', closed_by_pro: 'Chiusa',
};

const ProRichiestePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('incoming');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateMode, setDateMode] = useState('all');
  const [customDate, setCustomDate] = useState('');
  const [customMonth, setCustomMonth] = useState('');
  const [contactByReq, setContactByReq] = useState({}); // request_id -> contact data
  const [includeArchived, setIncludeArchived] = useState(false);
  const [recordReq, setRecordReq] = useState(null);
  const [proposeByReq, setProposeByReq] = useState({});
  const [detailReq, setDetailReq] = useState(null);

  const load = useCallback(async () => {
    const activeTab = TABS.find((t) => t.key === tab);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', activeTab.statuses);
      params.set('limit', '30');
      if (dateMode === 'day' && customDate) params.set('day', customDate);
      if (dateMode === 'month' && customMonth) params.set('month', customMonth);
      if (includeArchived) params.set('include_archived', 'true');
      const r = await api.get(`/api/professionals/me/requests?${params.toString()}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento richieste');
      setItems([]);
    } finally { setLoading(false); }
  }, [tab, dateMode, customDate, customMonth, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const runCron = async () => {
    try {
      const r = await api.post('/api/professionals/me/maintenance/run-cron');
      toast.success(`Cron: ${r.data.pending?.promoted_to_pending_pro ?? 0} → pending, ${r.data.archive?.archived ?? 0} archiviate`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore cron');
    }
  };

  const doAction = async (req, action, to = null) => {
    try {
      const params = new URLSearchParams({ action });
      if (to) params.set('to', to);
      await api.post(`/api/professionals/me/requests/${req.request_id}/action?${params.toString()}`);
      toast.success(action === 'accept' ? 'Richiesta accettata!' : action === 'close' ? 'Richiesta chiusa' : 'Spostata');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore azione');
    }
  };

  const proposeDate = async (req) => {
    const value = proposeByReq[req.request_id];
    if (!value) { toast.error('Scegli data e ora da proporre'); return; }
    try {
      await api.post(`/api/requests/${req.request_id}/appointment/propose`, { appointment_at: value });
      toast.success('Data proposta al cliente');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore proposta data');
    }
  };

  const showContact = async (req) => {    if (contactByReq[req.request_id]) {
      setContactByReq((s) => { const c = { ...s }; delete c[req.request_id]; return c; });
      return;
    }
    try {
      const r = await api.get(`/api/professionals/me/requests/${req.request_id}/client-contact`);
      setContactByReq((s) => ({ ...s, [req.request_id]: r.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Contatti non disponibili');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="pro-richieste-page">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-semibold text-4xl mb-2">Richieste dei clienti</h1>
            <p className="text-foreground/70">Ultime 30 richieste per categoria. Urgenti in cima. Auto-Pending dopo 48h non accettate, auto-archiviazione dopo 30 giorni.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-full" onClick={runCron} data-testid="run-cron-btn">
              <ArrowClockwise size={14} className="mr-1" /> Aggiorna stati
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/professional-dashboard')} data-testid="back-btn">
              ← Dashboard
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex flex-wrap gap-2" data-testid="pro-req-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground/70 border-border hover:bg-muted'}`}
                data-testid={`pro-req-tab-${t.key}`}
              >
                <Icon size={12} weight="duotone" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Date filter */}
        <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="pro-date-filter">
          <Funnel size={14} className="text-foreground/60" />
          <span className="text-xs text-foreground/60">Filtra:</span>
          {['all', 'day', 'month'].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setDateMode(mode)}
              className={`px-3 py-1 rounded-full text-xs border ${dateMode === mode ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
              data-testid={`pro-date-${mode}`}
            >
              {mode === 'all' ? 'Tutte' : mode === 'day' ? 'Giorno' : 'Mese'}
            </button>
          ))}
          {dateMode === 'day' && (
            <input type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} className="text-xs px-2 py-1 border rounded-full" data-testid="pro-date-input-day" />
          )}
          {dateMode === 'month' && (
            <input type="month" value={customMonth} onChange={(e) => setCustomMonth(e.target.value)} className="text-xs px-2 py-1 border rounded-full" data-testid="pro-date-input-month" />
          )}
          <label className="inline-flex items-center gap-1 text-xs text-foreground/70 ml-2 cursor-pointer">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="include-archived-toggle" />
            Includi archiviate
          </label>
        </div>

        {loading ? (
          <div className="text-center py-12 text-foreground/60">Caricamento…</div>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center rounded-2xl bg-muted/20" data-testid="pro-empty">
            <p className="text-foreground/70">Nessuna richiesta corrispondente ai filtri.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((req) => {
              const contact = contactByReq[req.request_id];
              const canAcceptClose = ['notified', 'pending_pro'].includes(req.status);
              const canMoveToAccepted = req.status === 'closed_by_pro';
              const canMoveToPending = req.status === 'closed_by_pro' || req.status === 'accepted';
              const canShowContact = !req.contact_locked;
              const needsDate = ['accepted', 'in_progress'].includes(req.status) && !req.appointment_at;
              const canMarkCompleted = ['accepted', 'in_progress'].includes(req.status);
              return (
                <Card key={req.request_id} className={`rounded-2xl p-5 ${req.is_urgent ? 'border-l-4 border-l-red-500' : ''} ${req.archived ? 'opacity-70' : ''}`} data-testid={`pro-req-${req.request_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-primary/10 text-primary">{STATUS_LABEL[req.status] || req.status}</Badge>
                      {req.is_urgent && (
                        <Badge className="bg-red-100 text-red-800">
                          <Lightning size={12} className="mr-1" weight="fill" /> URGENTE
                        </Badge>
                      )}
                      {req.archived && <Badge className="bg-slate-200 text-slate-700 text-[10px]">Archiviata</Badge>}
                      <span className="text-xs text-foreground/60">{new Date(req.created_at).toLocaleDateString('it-IT')} · {new Date(req.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <span className="text-[10px] font-mono text-foreground/50">{req.request_id}</span>
                  </div>

                  <div className="mb-3">
                    <div className="text-sm text-foreground/70 mb-1">
                      <strong>{req.contact_locked ? 'Dati cliente riservati' : (req.patient_name || req.requester_name || 'Paziente')}</strong> · {req.region || ''}{req.comune ? ` — ${req.comune}` : ''}
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(req.needs_labels || []).slice(0, 5).map((l, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{l}</Badge>
                      ))}
                    </div>
                    {req.description && <p className="text-sm text-foreground/80 line-clamp-2">{req.description}</p>}
                    {req.budget && (
                      <p className="text-xs text-foreground/60 mt-1">
                        Budget: {req.budget_type === 'monthly' ? `€${req.budget} (mensile)` : req.budget_type === 'single' ? `€${req.budget} (per prestazione)` : `a partire da €${req.budget}`}
                      </p>
                    )}
                  </div>

                  {req.appointment_at && (
                    <div className="mb-3 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm" data-testid={`appt-${req.request_id}`}>
                      <strong>Prestazione fissata:</strong>{' '}
                      {new Date(req.appointment_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      <div className="text-xs text-foreground/60 mt-1">
                        Ricorda di segnarla conclusa: dopo 24 ore i crediti riservati vengono scalati automaticamente.
                      </div>
                    </div>
                  )}

                  {user?.is_center && ['accepted', 'in_progress'].includes(req.status) && (
                    <AssignStaffBox request={req} onAssigned={load} />
                  )}

                  {needsDate && (
                    <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid={`needs-date-${req.request_id}`}>
                      <strong>In attesa che il cliente indichi la data.</strong> Puoi già contattarlo con i
                      dati qui sotto per concordarla. Se non arriva entro 3 giorni la richiesta
                      decade e i crediti riservati tornano disponibili.
                      <div className="mt-2 flex flex-wrap items-end gap-2">
                        <Input
                          type="datetime-local"
                          className="h-9 max-w-[220px]"
                          value={proposeByReq[req.request_id] || ''}
                          onChange={(e) => setProposeByReq((s) => ({ ...s, [req.request_id]: e.target.value }))}
                          data-testid={`propose-input-${req.request_id}`}
                        />
                        <Button size="sm" variant="outline" className="rounded-full h-9" onClick={() => proposeDate(req)} data-testid={`propose-btn-${req.request_id}`}>
                          Proponi questa data
                        </Button>
                      </div>
                      {req.proposed_appointment_at && (
                        <div className="text-xs text-amber-900 mt-2">
                          Data proposta al cliente:{' '}
                          {new Date(req.proposed_appointment_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} — in attesa di conferma
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-border/60">
                    <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setDetailReq(req)} data-testid={`detail-${req.request_id}`}>
                      <Info size={14} className="mr-1" /> Dettaglio Richiesta
                    </Button>
                    {canAcceptClose && (
                      <>
                        <Button size="sm" className="rounded-full h-8" onClick={() => doAction(req, 'accept')} data-testid={`accept-${req.request_id}`}>
                          <CheckCircle size={14} className="mr-1" /> Accetta
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => doAction(req, 'close')} data-testid={`close-${req.request_id}`}>
                          <X size={14} className="mr-1" /> Chiusa
                        </Button>
                      </>
                    )}
                    {canMarkCompleted && (
                      <Button size="sm" className="rounded-full h-8 bg-sky-600 hover:bg-sky-700" onClick={() => doAction(req, 'complete')} data-testid={`complete-${req.request_id}`}>
                        <CheckCircle size={14} className="mr-1" weight="fill" /> Marca completata
                      </Button>
                    )}
                    {canMoveToAccepted && (
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => doAction(req, 'move', 'accepted')} data-testid={`move-accepted-${req.request_id}`}>
                        <ArrowClockwise size={14} className="mr-1" /> Sposta in Accettate
                      </Button>
                    )}
                    {canMoveToPending && (
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => doAction(req, 'move', 'pending_pro')} data-testid={`move-pending-${req.request_id}`}>
                        <ArrowClockwise size={14} className="mr-1" /> Sposta in Pending
                      </Button>
                    )}
                    {canShowContact && (
                      <Button size="sm" variant={contact ? 'default' : 'outline'} className="rounded-full h-8" onClick={() => showContact(req)} data-testid={`contact-${req.request_id}`}>
                        <Phone size={14} className="mr-1" /> {contact ? 'Nascondi contatti' : 'Mostra contatti'}
                      </Button>
                    )}
                    {canShowContact && (
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setRecordReq(req)} data-testid={`record-${req.request_id}`}>
                        <FolderOpen size={14} className="mr-1" /> Cartella clinica
                      </Button>
                    )}
                  </div>

                  {contact && (
                    <div className="mt-3"><ClientContactCard c={contact} testId={`contact-block-${req.request_id}`} /></div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {recordReq && (
        <ClientRecordDialog
          request={recordReq}
          open={!!recordReq}
          onOpenChange={(o) => { if (!o) setRecordReq(null); }}
        />
      )}
      {detailReq && (
        <RequestDetailDialog
          request={detailReq}
          open={!!detailReq}
          onOpenChange={(o) => { if (!o) setDetailReq(null); }}
        />
      )}
    </div>
  );
};

export default ProRichiestePage;
